import React, { ReactElement, useCallback, useContext, useEffect } from 'react';

import { NavLink, useNavigate, useLocation } from 'react-router-dom';

import {
    Button,
    Container,
    Divider,
    Flex,
    Image,
    Spacer,
    Text,
    VStack,
} from '@chakra-ui/react';
import { ArrowBackIcon, ArrowForwardIcon, QuestionOutlineIcon, SearchIcon } from '@chakra-ui/icons';

import karlskronaLogoPath from '../../resources/karlskronaLogo.png';

// sidebar icon
import helpPath from '../../resources/help.png';
import homePath from '../../resources/home.png';
import applyPath from '../../resources/apply.png';
import susafPath from '../../resources/susaf.png';

import { SideBarContext } from '../../contexts/SideBarContext';

import './styles.css';
import { AuthenticationContext } from '../../contexts/AuthenticationContext';
import ToastBox from '../ToastBox';
import { ToastBoxContext } from '../../contexts/ToastBoxContext';

export interface SidebarMenu {
    path: string;
    title: string;
    icon: ReactElement;
    ariaLabel: string;
}

const sidebarMenus: SidebarMenu[] = [
    {
        path: '/',
        title: 'Home',
        icon: <Image
            src={homePath}
            height={4}
            width={4}
            alt="home"
        />,
        ariaLabel: 'home',
    },
    {
        path: '/warm_up',
        title: 'Warm Up',
        icon: <QuestionOutlineIcon />,
        ariaLabel: 'question',
    },
    {
        path: '/capture',
        title: 'Capture',
        icon: <QuestionOutlineIcon />,
        ariaLabel: 'capture',
    },
    {
        path: '/questions',
        title: 'Questions',
        icon: <QuestionOutlineIcon />,
        ariaLabel: 'question',
    },
    {
        path: '/analysis',
        title: 'Analysis',
        icon: <QuestionOutlineIcon />,
        ariaLabel: 'analysis',
    },
    {
        path: '/about',
        title: 'About',
        icon: <Image
            src={helpPath}
            height={4}
            width={4}
            alt="help"
        />,
        ariaLabel: 'help',
    },
    {
        path: '/synthesis',
        title: 'Synthesis',
        icon: <Image
            src={applyPath}
            height={4}
            width={4}
            alt="synthesis"
        />,
        ariaLabel: 'synthesis',
    },
];

const loggedInSidebarMenus: SidebarMenu[] = [
    {
        path: '/manage_dimensions',
        title: 'Manage Dimensions',
        icon: <SearchIcon />,
        ariaLabel: 'manage-users',
    },
    {
        path: '/manage_topics',
        title: 'Manage Topics',
        icon: <SearchIcon />,
        ariaLabel: 'manage-topics',
    },
    {
        path: '/manage_questions',
        title: 'Manage Questions',
        icon: <QuestionOutlineIcon />,
        ariaLabel: 'manage-question',
    },
];

function Sidebar() {
    const {
        sideBarNegativeTabIndex,
    } = useContext(SideBarContext);

    const {
        loggedIn,
        setLoggedIn,
    } = useContext(AuthenticationContext);
    const {
        toast,
        showToast,
        onCloseToast,
    } = useContext(ToastBoxContext);
    const navigate = useNavigate();

    const location = useLocation();

    useEffect(() => {
        const pathName = location.pathname;
        const loggedInSidebarMenusPaths = loggedInSidebarMenus.map((m) => m.path);
        const pathNameIndex = loggedInSidebarMenusPaths.indexOf(pathName);
        if (pathNameIndex <= -1) {
            return;
        }
        setLoggedIn.on();
    }, [location, setLoggedIn]);

    const onLogout = useCallback(
        () => {
            setLoggedIn.off();
            const successToastComponent = toast && toast({
                status: 'success',
                isClosable: true,
                variant: 'subtle',
                id: undefined,
                duration: null,
                position: 'top',
                render: () => (
                    <ToastBox
                        onCloseToast={onCloseToast}
                        title="Logged out successfully"
                        description="You have been successfully logged out."
                        status="success"
                    />
                ),
            });
            showToast(successToastComponent);
            navigate('/');
        },
        [
            navigate,
            onCloseToast,
            setLoggedIn,
            showToast,
            toast,
        ],
    );
    return (
        <VStack
            p={2}
            spacing={4}
            role="complementary"
        >
            <Image
                src={susafPath}
                alt="logo"
                role="img"
                width="40%"
            />
            <span
                role="heading"
                aria-level={1}
                style={{ fontSize: 20, fontWeight: 'bold', textAlign: 'center' }}
            >
                SUSAF
                <br />
                The Sustainability
                <br />
                Awareness Framework
            </span>
            {!loggedIn && sidebarMenus.map((menu) => (
                <Container
                    key={menu.title}
                    role="navigation"
                    width="100%"
                    display="flex"
                    id={menu.ariaLabel}
                    aria-label={menu.ariaLabel}
                    tabIndex={sideBarNegativeTabIndex ? -1 : undefined}
                >
                    <NavLink
                        to={menu.path}
                        className={({ isActive }) => (isActive ? 'nav-link-active' : 'nav-link')}
                        aria-labelledby={menu.ariaLabel}
                        tabIndex={sideBarNegativeTabIndex ? -1 : undefined}
                    >
                        <Flex
                            justifyContent="space-around"
                            alignItems="center"
                            tabIndex={sideBarNegativeTabIndex ? -1 : undefined}
                        >
                            {menu.icon}
                            <Text width="70%">
                                {menu.title}
                            </Text>
                        </Flex>
                    </NavLink>
                </Container>
            ))}
            <Spacer />
            <Divider />
            {!loggedIn && (
                <Container
                    key="Login"
                    role="navigation"
                    width="100%"
                    display="flex"
                    aria-label="login"
                    tabIndex={sideBarNegativeTabIndex ? -1 : undefined}
                >
                    <NavLink
                        to="/login"
                        className={({ isActive }) => (isActive ? 'nav-link-active' : 'nav-link')}
                        aria-labelledby="login"
                        tabIndex={sideBarNegativeTabIndex ? -1 : undefined}
                    >
                        <Flex
                            justifyContent="space-around"
                            alignItems="center"
                            tabIndex={sideBarNegativeTabIndex ? -1 : undefined}
                        >
                            <ArrowForwardIcon />
                            <Text width="70%">
                                Log in
                            </Text>
                        </Flex>
                    </NavLink>
                </Container>
            )}
            {loggedIn && loggedInSidebarMenus.map((menu) => (
                <Container
                    key={menu.title}
                    role="navigation"
                    width="100%"
                    display="flex"
                    id={menu.ariaLabel}
                    aria-label={menu.ariaLabel}
                    tabIndex={sideBarNegativeTabIndex ? -1 : undefined}
                >
                    <NavLink
                        to={menu.path}
                        className={({ isActive }) => (isActive ? 'nav-link-active' : 'nav-link')}
                        aria-labelledby={menu.ariaLabel}
                        tabIndex={sideBarNegativeTabIndex ? -1 : undefined}
                    >
                        <Flex
                            justifyContent="space-around"
                            alignItems="center"
                            tabIndex={sideBarNegativeTabIndex ? -1 : undefined}
                        >
                            {menu.icon}
                            <Text width="70%">
                                {menu.title}
                            </Text>
                        </Flex>
                    </NavLink>
                </Container>
            ))}
            {loggedIn && (
                <Button
                    justifyContent="space-between"
                    alignItems="center"
                    tabIndex={sideBarNegativeTabIndex ? -1 : undefined}
                    width="90%"
                    leftIcon={<ArrowBackIcon />}
                    onClick={onLogout}
                >
                    <Text width="76%" textAlign="left">
                        Log out
                    </Text>
                </Button>
            )}
            <Image
                src={karlskronaLogoPath}
                alt="logo"
                role="img"
                width="80%"
            />
        </VStack>
    );
}

export default Sidebar;
